import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { api, ApiError, guestCart, tokens } from '../api/client'

/**
 * Who is signed in.
 *
 * Phone plus one time code, and nothing else. There is no password to store, no email verification step and
 * no account creation form: the first successful code for a number creates the account, which is what a
 * grocery shop's customers expect.
 *
 * The guest basket is merged on sign in rather than discarded, because a customer who built a basket before
 * deciding to sign in has already decided to buy. The merge is a single call and it is the reason the cart
 * survives the sign in.
 */
interface Session {
  signedIn: boolean
  phoneNumber: string | null
  fullName: string | null
  email: string | null
  hasPassword?: boolean
  requestCode: (phoneNumber: string) => Promise<void>
  verifyCode: (phoneNumber: string, code: string) => Promise<void>
  loginWithPassword: (phoneNumber: string, password: string) => Promise<void>
  register: (phoneNumber: string, code: string, password: string, fullName?: string) => Promise<void>
  requestResetCode: (phoneNumber: string) => Promise<void>
  resetPassword: (phoneNumber: string, code: string, newPassword: string) => Promise<void>
  setPassword: (newPassword: string) => Promise<void>
  updateProfile: (data: { fullName?: string; email?: string }) => Promise<void>
  signOut: () => Promise<void>
  clearError: () => void
  busy: boolean
  error: string | null
}

const SessionContext = createContext<Session | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [signedIn, setSignedIn] = useState<boolean>(() => tokens.access() !== null)
  const [phoneNumber, setPhoneNumber] = useState<string | null>(null)
  const [fullName, setFullName] = useState<string | null>(null)
  const [email, setEmail] = useState<string | null>(null)
  const [hasPassword, setHasPassword] = useState<boolean>(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Fetch customer profile when signed in
  useEffect(() => {
    if (!signedIn) {
      setPhoneNumber(null)
      setFullName(null)
      setEmail(null)
      setHasPassword(false)
      return
    }

    let cancelled = false

    api.auth
      .profile()
      .then((profile) => {
        if (!cancelled) {
          setPhoneNumber(profile.phoneNumber)
          setFullName(profile.fullName ?? null)
          setEmail(profile.email ?? null)
          setHasPassword(Boolean((profile as { hasPassword?: boolean }).hasPassword))
        }
      })
      .catch((err) => {
        if (!cancelled) {
          // Only clear session on 401 Unauthorized
          if (err instanceof ApiError && err.status === 401) {
            tokens.clear()
            setSignedIn(false)
          }
        }
      })

    return () => {
      cancelled = true
    }
  }, [signedIn])

  const clearError = useCallback(() => setError(null), [])

  const establishSession = useCallback(
    async (result: { accessToken: string; refreshToken: string }, phone: string) => {
      tokens.set(result.accessToken, result.refreshToken)

      const guestCartId = guestCart.get()
      if (guestCartId) {
        try {
          await api.auth.mergeCart(guestCartId)
        } catch {
          // Keep going if merge fails
        } finally {
          guestCart.clear()
        }
      }

      setSignedIn(true)
      setPhoneNumber(phone)

      try {
        const prof = await api.auth.profile()
        setPhoneNumber(prof.phoneNumber)
        setFullName(prof.fullName ?? null)
        setEmail(prof.email ?? null)
        setHasPassword(Boolean((prof as { hasPassword?: boolean }).hasPassword))
      } catch {
        // Fallback
      }
    },
    [],
  )

  const requestCode = useCallback(async (phone: string) => {
    setBusy(true)
    setError(null)

    try {
      await api.auth.requestCode(phone)
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not send the code.')
      throw failure
    } finally {
      setBusy(false)
    }
  }, [])

  const verifyCode = useCallback(
    async (phone: string, code: string) => {
      setBusy(true)
      setError(null)

      try {
        const result = await api.auth.verifyCode(phone, code)
        await establishSession(result, phone)
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : 'That code was not accepted.')
        throw failure
      } finally {
        setBusy(false)
      }
    },
    [establishSession],
  )

  const loginWithPassword = useCallback(
    async (phone: string, password: string) => {
      setBusy(true)
      setError(null)

      try {
        const result = await api.auth.loginWithPassword(phone, password)
        await establishSession(result, phone)
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : 'Invalid phone number or password.')
        throw failure
      } finally {
        setBusy(false)
      }
    },
    [establishSession],
  )

  const register = useCallback(
    async (phone: string, code: string, password: string, fullName?: string) => {
      setBusy(true)
      setError(null)

      try {
        const result = await api.auth.register(phone, code, password, fullName)
        await establishSession(result, phone)
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : 'Registration failed.')
        throw failure
      } finally {
        setBusy(false)
      }
    },
    [establishSession],
  )

  const requestResetCode = useCallback(async (phone: string) => {
    setBusy(true)
    setError(null)

    try {
      await api.auth.requestResetCode(phone)
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not send password reset code.')
      throw failure
    } finally {
      setBusy(false)
    }
  }, [])

  const resetPassword = useCallback(
    async (phone: string, code: string, newPassword: string) => {
      setBusy(true)
      setError(null)

      try {
        const result = await api.auth.resetPassword(phone, code, newPassword)
        await establishSession(result, phone)
      } catch (failure) {
        setError(failure instanceof Error ? failure.message : 'Could not reset password.')
        throw failure
      } finally {
        setBusy(false)
      }
    },
    [establishSession],
  )

  const setPassword = useCallback(async (newPassword: string) => {
    setBusy(true)
    setError(null)

    try {
      await api.auth.setPassword(newPassword)
      setHasPassword(true)
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not set password.')
      throw failure
    } finally {
      setBusy(false)
    }
  }, [])

  const updateProfile = useCallback(async (data: { fullName?: string; email?: string }) => {
    setBusy(true)
    setError(null)
    try {
      const updated = await api.auth.updateProfile(data)
      setFullName(updated.fullName ?? null)
      setEmail(updated.email ?? null)
      setPhoneNumber(updated.phoneNumber)
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Could not update profile.')
      throw failure
    } finally {
      setBusy(false)
    }
  }, [])

  const signOut = useCallback(async () => {
    const refreshToken = tokens.refresh()

    tokens.clear()
    setSignedIn(false)
    setPhoneNumber(null)
    setFullName(null)
    setEmail(null)
    setHasPassword(false)

    if (refreshToken) {
      await api.auth.logout(refreshToken).catch(() => undefined)
    }
  }, [])

  const value = useMemo<Session>(
    () => ({
      signedIn,
      phoneNumber,
      fullName,
      email,
      hasPassword,
      requestCode,
      verifyCode,
      loginWithPassword,
      register,
      requestResetCode,
      resetPassword,
      setPassword,
      updateProfile,
      signOut,
      clearError,
      busy,
      error,
    }),
    [
      signedIn,
      phoneNumber,
      fullName,
      email,
      hasPassword,
      requestCode,
      verifyCode,
      loginWithPassword,
      register,
      requestResetCode,
      resetPassword,
      setPassword,
      updateProfile,
      signOut,
      clearError,
      busy,
      error,
    ],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): Session {
  const session = useContext(SessionContext)

  if (!session) {
    throw new Error('useSession must be used inside a SessionProvider')
  }

  return session
}
